<template>
  <ActionsMenu
    :actions="actions"
    @edit="edit"
    @clone="clone"
    @publish="publish"
    @archive="archive"
    @unarchive="unarchive"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import ActionsMenu from "@/components/ActionsMenu.vue";
import { customFormEditPath } from "@/mixins/path_helpers";

interface TemplateSummary {
  id: number;
  status: "published" | "draft" | "archived" | string;
}

const props = defineProps<{ template: TemplateSummary }>();
const store = useStore();
const router = useRouter();

const actions = computed(() => [
  {
    label: "Edit",
    icon: "fas fa-pencil-alt",
    eventName: "edit"
  },
  {
    label: "Clone",
    icon: "fas fa-clone",
    eventName: "clone"
  },
  {
    label: "Publish",
    icon: "fas fa-cloud-arrow-up",
    eventName: "publish",
    visible: props.template.status === "draft"
  },
  {
    label: "Archive",
    icon: "fas fa-box-archive",
    eventName: "archive",
    visible: props.template.status !== "archived"
  },
  {
    label: "Unarchive",
    icon: "fas fa-box-open",
    eventName: "unarchive",
    visible: props.template.status === "archived"
  }
]);

function edit() {
  router.push(customFormEditPath(props.template.id));
}

// Clone deep-copies the template server-side and drops the author straight into
// the new draft's builder — cloning is almost always a prelude to editing.
async function clone() {
  const template = await store.dispatch(
    "duplicateCustomFormTemplate",
    props.template.id
  );
  router.push(customFormEditPath(template.id));
}

// Lifecycle actions go through the shared store; the datatable still listens on
// `refresh-datatable`, so we keep emitting it after the dispatch resolves.
async function lifecycle(action: string) {
  await store.dispatch(action, props.template.id);
  EventBus.$emit("refresh-datatable");
}

// A publish can be rejected by the completeness gate (422); the global
// interceptor flashes the reason, so we swallow the rejection here to avoid an
// unhandled promise and to skip the list refresh on failure.
const publish = () => lifecycle("publishCustomFormTemplate").catch(() => {});
const archive = () => lifecycle("archiveCustomFormTemplate");
const unarchive = () => lifecycle("unarchiveCustomFormTemplate");
</script>
